import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiErrToStr, resolveImg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Shield, CheckCircle2 } from "lucide-react";

export default function BookingCheckout() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [entity, setEntity] = useState(null);
  const [f, setF] = useState({
    guest_name: user?.name || "", guest_email: user?.email || "", guest_phone: "",
    check_in: "", check_out: "", guests: 2, special_requests: "",
  });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.get(`/entities/${slug}`).then(r => setEntity(r.data.entity)).catch(() => nav("/"));
  }, [slug, nav]);

  const total = entity?.price ? Number(entity.price) * (Number(f.guests) || 1) : 0;
  const gst = Math.round(total * 0.05);
  const grand = total + gst;

  const pay = async () => {
    if (!f.guest_name || !f.guest_email || !f.guest_phone) return toast.error("Please fill your details");
    setBusy(true);
    // MOCKED Razorpay: simulate payment sheet
    await new Promise(r => setTimeout(r, 1200));
    try {
      const { data } = await api.post("/bookings", { ...f, entity_id: entity.id });
      setDone(data);
      toast.success("Payment successful (Test Mode)");
    } catch (err) { toast.error(apiErrToStr(err)); }
    finally { setBusy(false); }
  };

  if (!entity) return <div className="max-w-3xl mx-auto p-6"><Skeleton className="h-96 rounded-xl" /></div>;

  if (done) return (
    <div className="max-w-lg mx-auto p-6 text-center">
      <div className="bg-white rounded-2xl p-8 card-shadow border border-gold/20">
        <div className="w-16 h-16 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center mx-auto mb-3">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <h2 data-testid="booking-success" className="font-display text-2xl text-indigo-deep">Booking Confirmed</h2>
        <p className="text-sm text-indigo-deep/60 mt-1">Har Har Mahadev — your Yatra is booked.</p>
        <div className="mt-4 text-left text-sm bg-surfacealt/50 p-4 rounded-lg">
          <Row k="Booking ID" v={done.id.slice(0, 8).toUpperCase()} />
          <Row k="Payment ID" v={done.payment_id} />
          <Row k="Item" v={done.entity_name} />
          <Row k="Amount paid" v={`₹${Number(done.amount || 0).toLocaleString("en-IN")}`} />
        </div>
        <p className="text-xs text-indigo-deep/50 mt-4">A confirmation has been recorded. Our team will reach out with next steps.</p>
        <div className="mt-6 flex gap-2">
          <Button onClick={() => nav("/account")} className="flex-1 bg-indigo-deep text-white">View My Bookings</Button>
          <Button variant="outline" onClick={() => nav("/")} className="flex-1 border-gold/40">Home</Button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto p-4 md:p-6 grid md:grid-cols-[1fr_360px] gap-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-gold-dark">Secure Checkout</div>
        <h1 className="font-display text-2xl font-bold text-indigo-deep">Book — {entity.name}</h1>

        <div className="mt-6 bg-white rounded-xl p-6 card-shadow border border-gold/15 space-y-4">
          <h3 className="font-display text-lg text-indigo-deep">Guest details</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <F label="Full name"><Input data-testid="checkout-name" value={f.guest_name} onChange={(e) => setF({ ...f, guest_name: e.target.value })} /></F>
            <F label="Phone"><Input data-testid="checkout-phone" value={f.guest_phone} onChange={(e) => setF({ ...f, guest_phone: e.target.value })} /></F>
            <F label="Email"><Input data-testid="checkout-email" type="email" value={f.guest_email} onChange={(e) => setF({ ...f, guest_email: e.target.value })} /></F>
            <F label="Guests"><Input data-testid="checkout-guests" type="number" min="1" value={f.guests} onChange={(e) => setF({ ...f, guests: Number(e.target.value) })} /></F>
            <F label="Check-in / Start"><Input data-testid="checkout-checkin" type="date" value={f.check_in} onChange={(e) => setF({ ...f, check_in: e.target.value })} /></F>
            <F label="Check-out / End"><Input data-testid="checkout-checkout" type="date" value={f.check_out} onChange={(e) => setF({ ...f, check_out: e.target.value })} /></F>
          </div>
          <F label="Special requests"><Input data-testid="checkout-notes" value={f.special_requests} onChange={(e) => setF({ ...f, special_requests: e.target.value })} placeholder="Senior citizen, palki, pure-veg meals…" /></F>
        </div>
      </div>

      <aside>
        <div className="bg-white rounded-xl p-6 card-shadow border border-gold/20 sticky top-20">
          <div className="flex gap-3 items-start">
            <img src={resolveImg(entity.image)} alt="" className="w-16 h-16 rounded-lg object-cover" />
            <div>
              <div className="font-semibold text-indigo-deep">{entity.name}</div>
              <div className="text-xs text-indigo-deep/60">{entity.city}, {entity.state}</div>
            </div>
          </div>
          <div className="mt-5 space-y-2 text-sm">
            <Row k={`Base × ${f.guests}`} v={`₹${total.toLocaleString("en-IN")}`} />
            <Row k="GST (5%)" v={`₹${gst.toLocaleString("en-IN")}`} />
            <div className="spiritual-divider my-2" />
            <div className="flex justify-between font-display text-lg font-bold">
              <span className="text-indigo-deep">Total</span>
              <span className="text-saffron">₹{grand.toLocaleString("en-IN")}</span>
            </div>
          </div>
          <Button data-testid="pay-now-btn" onClick={pay} disabled={busy} className="w-full h-12 mt-5 bg-gold text-indigo-deep hover:bg-gold-dark hover:text-white font-semibold">
            {busy ? "Processing payment…" : `Pay ₹${grand.toLocaleString("en-IN")}`}
          </Button>
          <div className="mt-3 flex items-center justify-center gap-1 text-[11px] text-indigo-deep/60">
            <Shield className="w-3 h-3" /> Secured by Razorpay · <span className="text-gold-dark font-semibold">Test Mode</span>
          </div>
        </div>
      </aside>
    </div>
  );
}
const F = ({ label, children }) => <div><Label className="text-xs font-semibold uppercase tracking-wider text-indigo-deep/60">{label}</Label><div className="mt-1">{children}</div></div>;
const Row = ({ k, v }) => <div className="flex justify-between text-sm"><span className="text-indigo-deep/60">{k}</span><span className="text-indigo-deep font-medium">{v}</span></div>;
